import React, { useState, useEffect } from 'react';
import { X, Copy, ExternalLink, Link as LinkIcon, Check, Loader2, QrCode, FileDown, ImageDown, Sparkles } from 'lucide-react';
import { shortUrlAPI, eventAPI } from '../../api/endpoints';
import { useToastContext } from '../../context/ToastContext';
import Button from '../common/Button';
import QRCode from 'qrcode';

interface ShareEventLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: any;
  onDownloadQrPdf?: (event: any) => void;
  onDownloadQrPng?: (event: any) => void;
}

const ShareEventLinkModal: React.FC<ShareEventLinkModalProps> = ({
  isOpen,
  onClose,
  event,
  onDownloadQrPdf,
  onDownloadQrPng,
}) => {
  const toast = useToastContext();
  const [linkType, setLinkType] = useState<'attendee' | 'usher'>('attendee');
  const [shortenUrl, setShortenUrl] = useState<boolean>(true); // Default checked like modern UX
  const [shortUrl, setShortUrl] = useState<string>('');
  const [loadingShortUrl, setLoadingShortUrl] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

  const origin = window.location.origin;
  const longUrl = linkType === 'usher'
    ? `${origin}/self-register?event=${event?.slug || event?.id}&usher=true`
    : (event?.registration_token
        ? `${origin}/register?token=${event.registration_token}`
        : `${origin}/self-register?event=${event?.slug || event?.id}`);

  const currentDisplayUrl = (shortenUrl && linkType === 'attendee' && shortUrl)
    ? shortUrl
    : longUrl;

  // Fetch or generate short URL when modal opens or shorten toggle is enabled
  useEffect(() => {
    if (!isOpen || !event?.id) return;

    let isMounted = true;

    const fetchOrGenerateShortUrl = async () => {
      setLoadingShortUrl(true);
      try {
        const response = await shortUrlAPI.getAll(event.id);
        const rawData = response.data;
        const urlsList: any[] = Array.isArray(rawData) 
          ? rawData 
          : (Array.isArray(rawData?.data) ? rawData.data : []);
        
        const active = urlsList.find((u: any) => u.is_active && !u.is_expired) || urlsList[0];

        if (active && (active.short_code || active.short_url)) {
          const code = active.short_code;
          const urlToUse = code ? `${origin}/s/${code}` : active.short_url;
          if (isMounted) setShortUrl(urlToUse);
        } else {
          // Generate new short URL
          const attendeeLongUrl = event?.registration_token
            ? `${origin}/register?token=${event.registration_token}`
            : `${origin}/self-register?event=${event?.slug || event?.id}`;
          const createRes = await shortUrlAPI.create(event.id, { original_url: attendeeLongUrl });
          const createdItem = createRes.data?.data || createRes.data;
          const code = createdItem?.short_code;
          const newShortUrl = code ? `${origin}/s/${code}` : createdItem?.short_url;
          if (newShortUrl && isMounted) {
            setShortUrl(newShortUrl);
          }
        }
      } catch (err) {
        console.error('Failed to get/create short URL:', err);
      } finally {
        if (isMounted) setLoadingShortUrl(false);
      }
    };

    fetchOrGenerateShortUrl();
  }, [isOpen, event?.id, origin]);

  // Generate QR code data URL whenever currentDisplayUrl changes
  useEffect(() => {
    if (!currentDisplayUrl) return;
    QRCode.toDataURL(currentDisplayUrl, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' }
    })
      .then(url => setQrCodeDataUrl(url))
      .catch(err => console.error('Failed to generate QR preview:', err));
  }, [currentDisplayUrl]);

  if (!isOpen || !event) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentDisplayUrl);
      setCopied(true);
      toast.success(`Copied ${shortenUrl && linkType === 'attendee' ? 'shortened link' : 'link'} to clipboard!`);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error('Failed to copy link to clipboard');
    }
  };

  const handleOpenLink = () => {
    window.open(currentDisplayUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
          onClick={onClose}
        />

        {/* Modal Window */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all text-left sm:my-8">
          {/* Top Decorative Header */}
          <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 p-6 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2.5 mb-1 text-teal-100 font-medium text-xs tracking-wider uppercase">
              <LinkIcon className="h-4 w-4" />
              <span>Share Registration Link</span>
            </div>
            <h3 className="text-xl font-bold truncate text-white leading-tight">
              {event.name}
            </h3>
            {event.location && (
              <p className="text-xs text-teal-100/90 mt-1 truncate">
                {event.location}
              </p>
            )}
          </div>

          <div className="p-6 space-y-5">
            {/* Link Type Selector Tabs */}
            <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
              <button
                type="button"
                onClick={() => setLinkType('attendee')}
                className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all ${
                  linkType === 'attendee'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Attendee Registration Link
              </button>
              <button
                type="button"
                onClick={() => setLinkType('usher')}
                className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all ${
                  linkType === 'usher'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Usher Link
              </button>
            </div>

            {/* URL Input Box */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Event Link
              </label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  readOnly
                  value={loadingShortUrl && shortenUrl && linkType === 'attendee' ? 'Generating short URL...' : currentDisplayUrl}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="w-full pl-3 pr-24 py-3 bg-gray-50 border border-gray-300 rounded-xl text-xs font-mono text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 select-all"
                />
                <div className="absolute right-1.5 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleOpenLink}
                    title="Open in new tab"
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200/60 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <Button
                    size="sm"
                    onClick={handleCopy}
                    className={`gap-1.5 text-xs ${
                      copied
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-teal-600 hover:bg-teal-700 text-white'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Google Forms / Bitly Style Checkbox Toggle */}
            {linkType === 'attendee' && (
              <div className="bg-teal-50/70 border border-teal-200/80 rounded-xl p-3.5 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="shorten-url-toggle"
                  checked={shortenUrl}
                  onChange={(e) => setShortenUrl(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500 border-teal-300 rounded cursor-pointer accent-teal-600"
                />
                <label htmlFor="shorten-url-toggle" className="cursor-pointer select-none text-left">
                  <span className="text-xs font-bold text-teal-900 flex items-center gap-1.5">
                    Shorten URL
                    {loadingShortUrl && <Loader2 className="h-3 w-3 animate-spin text-teal-600" />}
                    <span className="bg-teal-200/80 text-teal-800 text-[10px] px-1.5 py-0.2 rounded font-semibold">Short URL</span>
                  </span>
                  <p className="text-[11px] text-teal-700/90 mt-0.5 leading-normal">
                    Create a clean, compact short link (<code className="bg-teal-100 px-1 py-0.5 rounded text-[10px] text-teal-900 font-mono">.../s/xYz123</code>) optimized for WhatsApp, SMS & flyers.
                  </p>
                </label>
              </div>
            )}

            {/* QR Code & Quick Download Card */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-white p-2 border border-gray-200 rounded-lg shadow-xs flex-shrink-0">
                {qrCodeDataUrl ? (
                  <img src={qrCodeDataUrl} alt="Event QR Code" className="w-16 h-16 object-contain" />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded">
                    <QrCode className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-1.5 text-left">
                <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <QrCode className="h-3.5 w-3.5 text-gray-600" />
                  Event QR Code
                </h4>
                <p className="text-[11px] text-gray-500">
                  Attendees can scan this QR code with their phones to open registration.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  {onDownloadQrPdf && (
                    <button
                      type="button"
                      onClick={() => onDownloadQrPdf(event)}
                      className="text-[11px] font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1 bg-teal-100/70 hover:bg-teal-200/70 px-2.5 py-1 rounded-md transition-colors"
                    >
                      <FileDown className="h-3 w-3" />
                      Download PDF Flyer
                    </button>
                  )}
                  {onDownloadQrPng && (
                    <button
                      type="button"
                      onClick={() => onDownloadQrPng(event)}
                      className="text-[11px] font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-1 bg-gray-200/70 hover:bg-gray-300/70 px-2.5 py-1 rounded-md transition-colors"
                    >
                      <ImageDown className="h-3 w-3" />
                      PNG Image
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex justify-end">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareEventLinkModal;
